import React, { useCallback, useContext, useEffect, useState, useRef, useMemo } from 'react';
import { Button, InputNumber, Select, Typography, message } from 'antd';
import DrawerCustom from '../../../DrawerCustom';
import type { BaseSelectRef } from 'rc-select';
import { CloseOutlined, CopyOutlined, DownloadOutlined } from '@ant-design/icons';
import { StripeContext } from 'lib.context/src/StripeContext';
import BillingStripeFormWrapper from '../../../BillingStripeForm';
import { PaymentMethodItem } from 'lib.store/src/models-to-replace/paymentMethods';
import { useGetUserBillingPaymentsQuery } from 'lib.store/src/main/payment/payment.api';
import { Link } from 'react-router-dom';
import { TERMS_LINK } from 'lib.pages/src/Authentification/Register/constants';
import { useAuth } from 'lib.context/src/AuthContext';
import { formatCurrency, formatCurrencyAmount } from 'lib.shared/src/numberFormat';
import { formatDate } from 'lib.shared/src/utils';
import StripeCertifiedPlug from '../../../BillingStripeForm/StripeCertifiedPlug';
import { BrandedCardItem } from 'lib.components';
import {
  useGetBillingInfoQuery,
  usePostWireInvoiceMutation,
} from 'lib.store/src/main/billing/billing.api';
import { BillingInfoSubscription, WireInvoiceResource } from 'lib.store/src/main/billing/models';
import { useAppSelector } from 'lib.hooks/src/redux';
import type { RootState } from 'lib.store/src';
import {
  PaymentMethodSelector,
  SubscriptionMonthsSelector,
  OrderSummary,
  WireInvoiceInstructions,
  useWireInvoicePreview,
  mapPreviewItemsToRows,
  buildSubscriptionTranslateMap,
} from 'lib.shared/src/billing/drawers/TopUpDrawer';
import 'lib.shared/src/billing/drawers/styles.scss';
import { TextBodySemiBold } from 'lib.typography';
import { getCurrencySymbol } from 'lib.shared/src/numberFormat';

type PaymentMethodOption = 'card' | 'wire_transfer';

interface EnableBoostProDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isLoading?: boolean;
}

const EnableBoostProDrawer: React.FC<EnableBoostProDrawerProps> = ({
  isOpen,
  onClose,
  onConfirm,
  isLoading,
}) => {
  const { user: fetchedUser } = useAuth();
  const userState = useAppSelector((state: RootState) => state.userAuth.user);
  const user = userState || fetchedUser;
  const {
    loading,
    intent,
    apiKey,
    selectedCardId,
    setSelectedCardId,
    onAddPaymentClick,
    onOpenStripeForm,
  } = useContext(StripeContext);

  const { data: billingInfo } = useGetBillingInfoQuery();
  const subscriptionTranslateMap = useMemo(
    () => buildSubscriptionTranslateMap(billingInfo?.data?.subscriptions),
    [billingInfo?.data?.subscriptions],
  );
  const boostProSubscription = billingInfo?.data?.subscriptions?.find(
    (s: BillingInfoSubscription) => s.plan?.id === 1,
  ) as BillingInfoSubscription;
  const boostProSubscriptionMoney = boostProSubscription?.money;
  const subscriptionPricePerMonth = boostProSubscriptionMoney?.price || 0;

  const currencyCode = user?.organization?.billing?.currency?.toUpperCase();
  const currencySymbol = useMemo(() => getCurrencySymbol(currencyCode), [currencyCode]);

  // Payment method state
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethodOption>('card');
  const [subscriptionMonths, setSubscriptionMonths] = useState<number>(1);
  const [balanceAmount, setBalanceAmount] = useState<number | null>(null);
  const [wireInvoice, setWireInvoice] = useState<WireInvoiceResource | null>(null);
  const [wireLoading, setWireLoading] = useState(false);

  const [postWireInvoice] = usePostWireInvoiceMutation();
  const { preview, previewLoading, fetchPreview, clearPreview } = useWireInvoicePreview();

  // Ref for the Select component to close dropdown
  const selectRef = useRef<BaseSelectRef>(null);

  useEffect(() => {
    if (isOpen && paymentMethod === 'card') {
      void onOpenStripeForm();
    }
  }, [onOpenStripeForm, isOpen, paymentMethod]);

  const { data: paymentMethodsResponse, isLoading: paymentsIsLoading } =
    useGetUserBillingPaymentsQuery();
  const [cards, setCards] = useState<PaymentMethodItem[] | []>([]);
  const [isUpdating, setisUpdating] = useState(false);

  const cardsList = cards.map((item) => {
    return {
      value: item.id,
      label: <BrandedCardItem {...item} isDefaultSubdued />,
    };
  });
  const options = [
    ...cardsList,
    {
      value: 'add',
      label: (
        <button
          className="no-style w-full"
          onClick={(e) => {
            e.stopPropagation();
            setSelectedCardId('add');
            selectRef.current?.blur();
          }}
        >
          + Add card
        </button>
      ),
    },
  ];

  const onCardChange = useCallback(
    (option: { value: string }) => {
      setSelectedCardId(option.value);
    },
    [setSelectedCardId],
  );

  useEffect(() => {
    if (paymentMethodsResponse?.data) {
      setCards(paymentMethodsResponse.data);
      const defaultCard = paymentMethodsResponse.data.find((card) => card.isDefault) || null;
      onCardChange({ value: defaultCard?.id || '' });
    }
  }, [paymentMethodsResponse?.data, onCardChange]);

  // Build wire invoice body
  const buildWireBody = useCallback(() => {
    return {
      subscription_type: boostProSubscription?.plan?.id || 1,
      subscription_months: subscriptionMonths,
      ...(balanceAmount && balanceAmount > 0 ? { balance_amount: balanceAmount } : {}),
    };
  }, [subscriptionMonths, balanceAmount, boostProSubscription?.plan?.id]);

  // Fetch preview when wire params change
  useEffect(() => {
    if (paymentMethod !== 'wire_transfer' || !isOpen) {
      clearPreview();
      return;
    }
    fetchPreview(buildWireBody());
  }, [paymentMethod, isOpen, buildWireBody, fetchPreview, clearPreview]);

  const previewRows = useMemo(
    () =>
      preview?.data?.items
        ? mapPreviewItemsToRows(preview.data.items, subscriptionTranslateMap, currencyCode ?? 'USD')
        : [],
    [preview, subscriptionTranslateMap, currencyCode],
  );

  const onSubmitCard = async () => {
    if (!selectedCardId) {
      void message.error('Please select a payment method');
      return;
    }

    setisUpdating(true);

    if (selectedCardId === 'add') {
      return await onAddPaymentClick()
        .then(() => {
          onConfirm();
        })
        .catch((error) => {
          console.error('Error adding payment method:', error);
          void message.error('Failed to add payment method');
        })
        .finally(() => setisUpdating(false));
    }

    setisUpdating(false);
    return onConfirm();
  };

  const onSubmitWire = async () => {
    setWireLoading(true);
    try {
      const response = await postWireInvoice(buildWireBody());
      if ('data' in response) {
        setWireInvoice(response.data.data);
        void message.success('Wire transfer invoice created');
      }
    } catch (error) {
      console.error('Error creating wire invoice:', error);
      void message.error('Failed to create wire invoice');
    } finally {
      setWireLoading(false);
    }
  };

  const handleCopyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      void message.success(`${label} copied to clipboard`);
    } catch {
      void message.error('Failed to copy to clipboard');
    }
  };

  const handleCopyBankDetails = () => {
    if (!wireInvoice?.bank_details) return;
    const { beneficiary, bank } = wireInvoice.bank_details;
    const details = [
      `Beneficiary: ${beneficiary.company_name}`,
      `Address: ${beneficiary.address}`,
      `Account Number: ${bank.account_number}`,
      bank.routing_number ? `Routing Number: ${bank.routing_number}` : null,
      `Bank: ${bank.name}`,
      `SWIFT/BIC: ${bank.swift_bic}`,
      bank.iban ? `IBAN: ${bank.iban}` : null,
      bank.intermediary_bank ? `Intermediary Bank: ${bank.intermediary_bank}` : null,
      bank.intermediary_swift_bic ? `Intermediary SWIFT: ${bank.intermediary_swift_bic}` : null,
      `Reference: ${wireInvoice.reference}`,
    ]
      .filter(Boolean)
      .join('\n');
    void handleCopyToClipboard(details, 'Bank details');
  };

  const handleDownloadInvoice = () => {
    if (!wireInvoice?.link) return;
    window.open(wireInvoice.link, '_blank');
  };

  const handleClose = () => {
    setWireInvoice(null);
    setPaymentMethod('card');
    setSubscriptionMonths(1);
    setBalanceAmount(null);
    clearPreview();
    onClose();
  };

  // ── Wire Invoice Instructions View ─────────────────────────────────
  if (wireInvoice) {
    return (
      <DrawerCustom
        title="Wire transfer invoice"
        open={isOpen}
        onClose={handleClose}
        placement="right"
        extra={<CloseOutlined onClick={handleClose} />}
        closeIcon={false}
        autoFocus={false}
        width={450}
        rootStyle={{ top: 'var(--shell-top, 62px)' }}
        footer={
          <footer className="drawer-footer">
            <Button size="large" block onClick={handleCopyBankDetails} icon={<CopyOutlined />}>
              Copy bank details
            </Button>
            <Button
              size="large"
              block
              type="primary"
              onClick={handleDownloadInvoice}
              icon={<DownloadOutlined />}
            >
              Download invoice
            </Button>
          </footer>
        }
      >
        <div className="drawer-content" data-test-id="wire-invoice-pro-drawer">
          <WireInvoiceInstructions
            wireInvoice={wireInvoice}
            onCopyToClipboard={handleCopyToClipboard}
            translateMap={subscriptionTranslateMap}
            currencyCode={currencyCode}
          />
        </div>
      </DrawerCustom>
    );
  }

  // ── Main Drawer ────────────────────────────────────────────────────
  return (
    <DrawerCustom
      title="Enable Boost PRO"
      open={isOpen}
      onClose={handleClose}
      placement="right"
      extra={<CloseOutlined onClick={handleClose} />}
      closeIcon={false}
      autoFocus={false}
      width={450}
      rootStyle={{ top: 'var(--shell-top, 62px)' }}
      footer={
        <footer className="drawer-footer">
          <Button size="large" block type="text" onClick={handleClose}>
            Cancel
          </Button>
          {paymentMethod === 'card' ? (
            <Button
              size="large"
              block
              type="primary"
              // PRO's own call: the PRO gradient
              className="btn-pro"
              // eslint-disable-next-line @typescript-eslint/no-misused-promises
              onClick={onSubmitCard}
              loading={isLoading || isUpdating}
              disabled={!selectedCardId && !!user?.organization?.billing?.id}
              data-test-id="enable-pro-confirm-button"
            >
              {!user?.organization?.billing?.id ? 'Set up billing and enable PRO' : 'Enable PRO'}
            </Button>
          ) : (
            <Button
              size="large"
              block
              type="primary"
              // eslint-disable-next-line @typescript-eslint/no-misused-promises
              onClick={onSubmitWire}
              loading={wireLoading}
              data-test-id="enable-pro-wire-button"
            >
              Create invoice
            </Button>
          )}
        </footer>
      }
    >
      <div className="drawer-content">
        {/* ── Payment Method Selector ─────────────────────────────── */}
        <div className="mb-24">
          <PaymentMethodSelector value={paymentMethod} onChange={setPaymentMethod} />
        </div>

        {/* ── Card Payment Mode ───────────────────────────────────── */}
        {paymentMethod === 'card' && (
          <>
            <div className="flex justify-space-between mb-12">
              <article className="flex-col text-subdued">
                <span>The monthly amount starting</span>
                <span>{formatDate(new Date())}</span>
              </article>
              <article className="flex-col text-title">
                <span></span>
                <span>{formatCurrency(String(boostProSubscriptionMoney?.price ?? 0), 'USD')}</span>
              </article>
            </div>

            <p className="mb-6 text-subdued">Payment card</p>

            <Select
              ref={selectRef}
              labelInValue
              onChange={onCardChange}
              value={{ value: selectedCardId }}
              style={{ width: '100%' }}
              options={options}
              className="mb-24"
              loading={paymentsIsLoading || loading}
            />

            <div className="drawer-disclaimer-grey mb-24">
              <span>
                By enabling a subscription, you authorize <br /> Boost.Express to charge your card
                for current and future payments as per our{' '}
                <Link className="link-style" to={TERMS_LINK} target={'_blank'}>
                  Terms and Conditions
                </Link>
              </span>
            </div>

            {selectedCardId !== 'add' && <StripeCertifiedPlug />}
            {intent && (
              <div
                className={selectedCardId === 'add' ? '' : 'drawer-content--billing-form-hidden'}
              >
                <BillingStripeFormWrapper intent={intent} apiKey={apiKey} />
              </div>
            )}
            {!intent && user?.organization?.billing?.id && loading && (
              <div className="text-center py-24">
                <p>Loading payment form...</p>
              </div>
            )}
            {!intent && user?.organization?.billing?.id && !loading && (
              <div className="text-center py-24">
                <Typography.Text type="danger">
                  Failed to load payment form. Please try again.
                </Typography.Text>
              </div>
            )}
          </>
        )}

        {/* ── Wire Transfer Mode ──────────────────────────────────── */}
        {paymentMethod === 'wire_transfer' && (
          <>
            <div className="subscription-addon mb-24">
              <div className="subscription-addon__header mb-0">
                <div className="subscription-addon__info">
                  <TextBodySemiBold className="text-neutral-10">
                    Boost PRO subscription
                  </TextBodySemiBold>
                  <span className="subscription-addon__sub">
                    {formatCurrencyAmount(subscriptionPricePerMonth, currencyCode ?? 'USD', {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                    })}
                    /month · No auto-renewal
                  </span>
                </div>
              </div>
            </div>

            <div className="mb-24">
              <SubscriptionMonthsSelector
                months={subscriptionMonths}
                onChange={setSubscriptionMonths}
              />
            </div>

            {/* Optional balance top-up */}
            <div className="mb-24">
              <span className="topup-label">Add a balance top-up (optional)</span>
              <InputNumber<number>
                className="topup-amount-input"
                value={balanceAmount}
                prefix={currencySymbol}
                min={0}
                formatter={(value) => `${value || ''}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                parser={(value) =>
                  value?.replace(/[^\d.]/g, '').replace(/,/g, '') as unknown as number
                }
                onChange={(v) => setBalanceAmount(v)}
                placeholder="0"
              />
            </div>

            {/* Order Summary from server preview */}
            {(previewRows.length > 0 || previewLoading) && (
              <OrderSummary
                items={previewRows}
                total={preview?.data?.total ?? 0}
                currencyCode={currencyCode}
                isLoading={previewLoading}
              />
            )}

            <div className="drawer-disclaimer-grey mt-16">
              <span>
                A wire transfer invoice will be created with bank details for payment. Your
                subscription will be activated once the payment is received and processed.
              </span>
            </div>
          </>
        )}
      </div>
    </DrawerCustom>
  );
};

export default EnableBoostProDrawer;
